// ============================================================================
// cost-line-partition.js — split a cost breakdown's line totals into "final"
// (a rate-book-matched charge-out price; inc-GST; must NOT be marked up) vs
// "markup" (AI-estimated direct cost that the existing contingency / overhead /
// margin / GST stack applies to). Pure + side-effect free.
// ----------------------------------------------------------------------------
// Slice 0 of the Pricing Engine workstream (DEC-PE-007 / DEC-PE-008). This is
// the NO-OP FOUNDATION: it is exercised by tests but NOT yet wired into the
// live cost path. The flag-gated authority-seam fix (`qm_rate_final_no_markup`,
// default off) will consume it inside cost-math / mapScopeToInputs so matched
// lines pass through at their final price while unmatched lines keep the markup.
// See docs/pricing-engine/SLICE-0-COUNCIL-REVIEW.md (binding conditions B1-B9).
//
// A line is "final" when it carries `rateIsFinalChargeOut === true` (current)
// or the legacy `rateIsFinalIncGst === true`, unless pending resolution has
// revoked that authority. Explicit builder-manual rows retain their authority.
// Current rows declare whether the charge-out is ex- or inc-GST; mixing those
// bases into one bucket was the source of QuoteMaker's 9% under-pricing against
// Francis's Skill.
// ============================================================================

import { pricingRowHasPendingResolution } from './pricing-resolution.js';

const PRIMARY_KEYS = ['materials', 'labour', 'equipment', 'subcontractors'];

function round2(n) {
    return Math.round((Number(n) || 0) * 100) / 100;
}

function lineTotal(line) {
    return Number(line?.total) || 0;
}

function hasManualCommercialAuthority(line) {
    const pricingStatus = String(line?.pricingStatus ?? line?.pricing_status ?? '').trim().toLowerCase();
    const rateSource = String(line?._rateSource ?? line?._rate_source ?? '').trim().toLowerCase();
    return line?.manualOverride === true
        || line?.manual_override === true
        || ['manual_override', 'manual_cost_estimate'].includes(pricingStatus)
        || ['project_manual', 'manual', 'manual_override', 'project_cost_estimate'].includes(rateSource);
}

function isFinalChargeOut(line) {
    if (pricingRowHasPendingResolution(line) && !hasManualCommercialAuthority(line)) return false;
    return line?.rateIsFinalChargeOut === true || line?.rateIsFinalIncGst === true;
}

/**
 * Partition a costBreakdown's line totals by whether they are final inc-GST
 * rate-book prices. `otherCosts` and `other` are aliases — whichever is present
 * is used (otherCosts preferred), mirroring mapScopeToInputs' `cb.otherCosts || cb.other`.
 *
 * @param {object} costBreakdown
 * @returns {{ finalIncGstTotal: number, finalExGstTotal: number, markupDirectTotal: number, hasFinalLines: boolean, lineSumTotal: number }}
 */
export function partitionCostLines(costBreakdown) {
    let finalIncGstTotal = 0;
    let finalExGstTotal = 0;
    let markupDirectTotal = 0;
    let hasFinalLines = false;

    if (costBreakdown && typeof costBreakdown === 'object') {
        const keys = [...PRIMARY_KEYS];
        if (Array.isArray(costBreakdown.otherCosts)) keys.push('otherCosts');
        else if (Array.isArray(costBreakdown.other)) keys.push('other');

        for (const key of keys) {
            const lines = costBreakdown[key];
            if (!Array.isArray(lines)) continue;
            for (const line of lines) {
                const total = lineTotal(line);
                if (isFinalChargeOut(line)) {
                    const taxBasis = String(line?.rateTaxBasis || line?.rate_tax_basis || '').toLowerCase();
                    if (taxBasis === 'ex_gst' && line?.rateIsFinalIncGst !== true) {
                        finalExGstTotal += total;
                    } else {
                        finalIncGstTotal += total;
                    }
                    hasFinalLines = true;
                } else {
                    markupDirectTotal += total;
                }
            }
        }
    }

    finalIncGstTotal = round2(finalIncGstTotal);
    finalExGstTotal = round2(finalExGstTotal);
    markupDirectTotal = round2(markupDirectTotal);
    return {
        finalIncGstTotal,
        finalExGstTotal,
        markupDirectTotal,
        hasFinalLines,
        // NO-OP invariant: equals the old line-sum directCost for any breakdown.
        lineSumTotal: round2(finalIncGstTotal + finalExGstTotal + markupDirectTotal),
    };
}
